import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { DataRoleMapper } from '../mappers/DataRoleMapper';
import { DataRoleDefinition } from '../models/ContentDefinitions';
import { IDataRoleDetailInfo, IDataRoleInfo } from '../models/IDataRoleInfo';
import { Module } from '../Module';
import { DeleteDataRolesRequest } from '../ServerApi/requests/DeleteDataRolesRequest';
import { GetDataRoleActionsRequest } from '../ServerApi/requests/GetDataRoleActionsRequest';
import { GetDataRolesRequest } from '../ServerApi/requests/GetDataRolesRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { MoveDataRolesToProjectRequest } from '../ServerApi/requests/MoveDataRolesToProjectRequest';
import { SetDataRolesOwnerRequest } from '../ServerApi/requests/SetDataRolesOwnerRequest';
import { IDataRoleActions } from '../ServerApi/types/IDataRoleActions';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { ServerService } from '../services/ServerService';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';

export class DataRoles implements IListResource<IDataRoleInfo>, IFieldValuesResource, IDetailResource<IDataRoleDetailInfo>, IActionResource<IDataRoleActions> {

  public static $inject = [
    'ErrorNotifyingServer',
    'FetcherFactory',
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<IDataRoleInfo>;
  private allQuery: ResourceQuery<IDataRoleInfo>;
  private detailFetcher: IDetailFetcher<IDataRoleDetailInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor(
    private ErrorNotifyingServer: ErrorNotifyingServer,
    FetcherFactory: FetcherFactory
  ) {
    this.server = ErrorNotifyingServer.getServerService();
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetDataRolesRequest, ServerApiMapper.dataRoles);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = FetcherFactory.getServerDetailFetcher('getDataRole', DataRoleMapper.mapDetail);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<IDataRoleInfo> {
    return this.allQuery;
  }

  public getById(id: string): ng.IPromise<IDataRoleDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'dataRole');
  }

  // Actions
  public actions(id: string): ng.IPromise<IDataRoleActions> {
    return this.server.sendRequest(new GetDataRoleActionsRequest(id));
  }

  public deleteDataRoles(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteDataRolesRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public moveToProject(targetProjectId: string, dataRoleIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(dataRoleIds, (idsBlock) => new MoveDataRolesToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetDataRolesOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public static isDataRoleDetailedInfo(item: any): item is IDataRoleDetailInfo {
    return item &&
           item.contentType &&
           item.contentType === DataRoleDefinition.contentType &&
           item.hasOwnProperty('dataTypes');
  }
}

Module.instance().service('DataRoles', DataRoles);
